import React, { useState } from "react";
import { Mail, Phone, Calendar as CalIcon, Send, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { createLead, createBooking } from "@/lib/api";
import { toast } from "sonner";

const TIME_SLOTS = [
    "09:00",
    "10:00",
    "11:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
];

export default function Contact() {
    const [activeTab, setActiveTab] = useState("contact");
    return (
        <section id="contact" className="section relative" data-testid="contact-section">
            <div className="container-x">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left side */}
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#C8A064]" />
                            <span className="overline">Get In Touch</span>
                        </div>
                        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#F3F4F6] leading-tight">
                            Start the <em className="text-[#C8A064] not-italic">conversation.</em>
                        </h2>
                        <p className="mt-6 text-[#94A3B8] leading-relaxed">
                            Whether you're ready to open a Vantage Markets account, want a
                            personalised platform walkthrough, or simply have a question — our team
                            is reachable in your language and time zone.
                        </p>

                        <div className="mt-12 space-y-7">
                            <ContactItem
                                icon={<Mail size={18} />}
                                label="Email"
                                value="hello@nexareach.com"
                                testId="contact-email"
                            />
                            <ContactItem
                                icon={<Phone size={18} />}
                                label="Direct Line"
                                value="+44 7456 443531"
                                testId="contact-phone"
                            />
                        </div>

                        <div className="mt-12 card-glass p-6 border-l-2 border-l-[#C8A064]">
                            <div className="overline mb-2">Office Hours</div>
                            <p className="text-sm text-[#94A3B8]">
                                Mon–Fri · 08:00 – 20:00 (EST)
                                <br />
                                Calls scheduled outside hours on request.
                            </p>
                        </div>
                    </div>

                    {/* Right side – Tabs + Forms */}
                    <div className="lg:col-span-7">
                        <div className="flex border-b border-white/10 mb-8">
                            <TabBtn
                                active={activeTab === "contact"}
                                onClick={() => setActiveTab("contact")}
                                testId="tab-contact"
                            >
                                Send a Message
                            </TabBtn>
                            <TabBtn
                                active={activeTab === "booking"}
                                onClick={() => setActiveTab("booking")}
                                testId="tab-booking"
                            >
                                Book a Call
                            </TabBtn>
                        </div>

                        {activeTab === "contact" ? <ContactForm /> : <BookingForm />}
                    </div>
                </div>
            </div>
        </section>
    );
}

const TabBtn = ({ active, onClick, children, testId }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className={`px-6 py-4 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
            active
                ? "border-[#C8A064] text-[#C8A064]"
                : "border-transparent text-[#94A3B8] hover:text-[#F3F4F6]"
        }`}
    >
        {children}
    </button>
);

const ContactItem = ({ icon, label, value, testId }) => (
    <div className="flex items-start gap-5" data-testid={testId}>
        <div className="w-11 h-11 border border-[#C8A064]/40 flex items-center justify-center text-[#C8A064] shrink-0">
            {icon}
        </div>
        <div>
            <div className="overline mb-1">{label}</div>
            <div className="text-[#F3F4F6] text-base">{value}</div>
        </div>
    </div>
);

/* -------- Contact Form -------- */
const ContactForm = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        interest: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Please provide your name and email.");
            return;
        }
        setLoading(true);
        try {
            await createLead(form);
            toast.success("Thank you — we'll be in touch within one business day.");
            setForm({
                name: "",
                email: "",
                phone: "",
                country: "",
                interest: "",
                message: "",
            });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field>
                    <Label>Full Name *</Label>
                    <input
                        value={form.name}
                        onChange={update("name")}
                        className="np-input"
                        placeholder="Jane Doe"
                        required
                        data-testid="contact-name-input"
                    />
                </Field>
                <Field>
                    <Label>Email *</Label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="np-input"
                        placeholder="jane@example.com"
                        required
                        data-testid="contact-email-input"
                    />
                </Field>
                <Field>
                    <Label>Phone</Label>
                    <input
                        value={form.phone}
                        onChange={update("phone")}
                        className="np-input"
                        placeholder="+1 555 ..."
                        data-testid="contact-phone-input"
                    />
                </Field>
                <Field>
                    <Label>Country</Label>
                    <input
                        value={form.country}
                        onChange={update("country")}
                        className="np-input"
                        placeholder="United States"
                        data-testid="contact-country-input"
                    />
                </Field>
            </div>

            <Field>
                <Label>I'm interested in</Label>
                <select
                    value={form.interest}
                    onChange={update("interest")}
                    className="np-input appearance-none"
                    data-testid="contact-interest-select"
                >
                    <option value="">Select an option</option>
                    <option>Opening a Vantage Markets account</option>
                    <option>Platform walkthrough</option>
                    <option>Account type comparison</option>
                    <option>IB / Partnership program</option>
                    <option>General inquiry</option>
                </select>
            </Field>

            <Field>
                <Label>Message</Label>
                <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    className="np-input resize-none"
                    placeholder="Tell us a little about your trading background and what you'd like to discuss..."
                    data-testid="contact-message-input"
                />
            </Field>

            <button
                type="submit"
                disabled={loading}
                className="btn-brass disabled:opacity-60"
                data-testid="contact-submit-btn"
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {loading ? "Sending..." : "Send Message"}
            </button>

            <p className="text-xs text-[#64748B] pt-2">
                By submitting, you consent to NexaReach LLC contacting you regarding Vantage
                Markets services. Risk warning: trading derivatives carries risk.
            </p>
        </form>
    );
};

/* -------- Booking Form -------- */
const BookingForm = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [date, setDate] = useState(undefined);
    const [time, setTime] = useState("");
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !date || !time) {
            toast.error("Please fill name, email, choose a date and a time slot.");
            return;
        }
        setLoading(true);
        try {
            await createBooking({
                ...form,
                preferred_date: date.toISOString().split("T")[0],
                preferred_time: time,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            toast.success(`Call booked for ${date.toDateString()} at ${time}.`);
            setForm({ name: "", email: "", phone: "", notes: "" });
            setDate(undefined);
            setTime("");
        } catch (err) {
            toast.error("Could not book. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field>
                    <Label>Full Name *</Label>
                    <input
                        value={form.name}
                        onChange={update("name")}
                        className="np-input"
                        placeholder="Jane Doe"
                        required
                        data-testid="booking-name-input"
                    />
                </Field>
                <Field>
                    <Label>Email *</Label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="np-input"
                        placeholder="jane@example.com"
                        required
                        data-testid="booking-email-input"
                    />
                </Field>
                <Field className="md:col-span-2">
                    <Label>Phone</Label>
                    <input
                        value={form.phone}
                        onChange={update("phone")}
                        className="np-input"
                        placeholder="+1 555 ..."
                        data-testid="booking-phone-input"
                    />
                </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-7">
                    <Label className="flex items-center gap-2 mb-3">
                        <CalIcon size={14} className="text-[#C8A064]" /> Choose Date *
                    </Label>
                    <div className="card-solid p-3" data-testid="booking-calendar-wrap">
                        <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(d) => d < today}
                            className="text-[#F3F4F6]"
                        />
                    </div>
                </div>
                <div className="md:col-span-5">
                    <Label className="mb-3">Preferred Time *</Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="booking-time-slots">
                        {TIME_SLOTS.map((slot) => (
                            <button
                                type="button"
                                key={slot}
                                onClick={() => setTime(slot)}
                                data-testid={`time-slot-${slot}`}
                                className={`py-3 text-sm border transition-colors ${
                                    time === slot
                                        ? "border-[#C8A064] bg-[#C8A064]/10 text-[#C8A064]"
                                        : "border-white/10 text-[#94A3B8] hover:border-[#C8A064]/50 hover:text-[#F3F4F6]"
                                }`}
                            >
                                {slot}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-[#64748B] mt-3">
                        Times shown in your local timezone.
                    </p>
                </div>
            </div>

            <Field>
                <Label>Anything we should know?</Label>
                <textarea
                    value={form.notes}
                    onChange={update("notes")}
                    rows={4}
                    className="np-input resize-none"
                    placeholder="Trading experience, focus markets, specific questions..."
                    data-testid="booking-notes-input"
                />
            </Field>

            <button
                type="submit"
                disabled={loading}
                className="btn-brass disabled:opacity-60"
                data-testid="booking-submit-btn"
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <CalIcon size={14} />}
                {loading ? "Booking..." : "Confirm Booking"}
            </button>
        </form>
    );
};

const Field = ({ children, className = "" }) => (
    <div className={`flex flex-col gap-2 ${className}`}>{children}</div>
);

const Label = ({ children, className = "" }) => (
    <label className={`overline ${className}`}>{children}</label>
);
